import { createContext, useContext, useEffect } from 'react';

/**
 * ShellContext — present whenever a page renders inside the redesign's app
 * shell (AppShell) or the public shell (PublicShell).
 *
 *   inShell     true — the legacy <Toolbar> and PortfolioOverview's own <Nav>
 *               read this and render nothing, so a page never shows two headers.
 *   isPhone     the ≤ 720 px layout is active
 *   setPhoneHeader({ title, back, action }) / resetPhoneHeader()
 *               a page overrides the phone header (title, back target, right
 *               action button). Use the usePhoneHeader() hook.
 *   risk        { halted, global, user, reason, loaded, refresh, polling } — the
 *               global halt state the header chip shows (null in PublicShell).
 *               `polling`: the shell polls the shared ['risk'] query, so a
 *               page reading it (ui/data useRiskQuery) must not poll too.
 *   socket      the shell's socket.io connection (useRiskWatch; null until it
 *               connects, and in PublicShell). Pages attach listeners to it
 *               rather than opening their own.
 */
export const ShellContext = createContext(null);

export const useShell = () => useContext(ShellContext);

/** True inside AppShell / PublicShell. */
export const useInShell = () => Boolean(useContext(ShellContext));

/**
 * usePhoneHeader({ title, back, action }) — set the phone header while the
 * calling page is mounted. `back`: a path string or a function; `action`: a
 * node (e.g. the 40 px "+" button). Pass a stable object (useMemo) or
 * primitives; it re-applies when title/back change.
 */
export function usePhoneHeader(config) {
  const shell = useContext(ShellContext);
  const title = config && config.title;
  const back = config && config.back;
  const action = config && config.action;
  useEffect(() => {
    if (!shell || !shell.setPhoneHeader) return undefined;
    shell.setPhoneHeader({ title, back, action });
    return () => shell.resetPhoneHeader();
    // `action` is a node; re-running on every render would loop, so it is
    // captured when title/back change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell && shell.setPhoneHeader, title, back]);
}
